import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaRegCopy, FaWhatsapp } from "react-icons/fa";
import { LuShare2 } from "react-icons/lu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import {
  buildPublicInviteShareMessage,
  openWhatsAppShare,
} from "@/utils/whatsappShare";

export type CycleShareMessageVariant = "poll" | "cycle";

interface CycleSharePanelProps {
  ready: boolean;
  shareUrl: string | null;
  sharePassword?: string;
  groupName?: string;
  settingUp: boolean;
  messageVariant?: CycleShareMessageVariant;
  onSetupPassword: (password: string) => Promise<string | null>;
  onDone?: () => void;
  showDone?: boolean;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const canUseNativeShare =
  typeof navigator !== "undefined" && typeof navigator.share === "function";

export function CycleSharePanel({
  ready,
  shareUrl,
  sharePassword = "",
  groupName,
  settingUp,
  messageVariant = "poll",
  onSetupPassword,
  onDone,
  showDone = true,
}: CycleSharePanelProps) {
  const { t } = useTranslation();
  const { success, error } = useEnhancedToast();
  const [password, setPassword] = useState("");
  const [localReady, setLocalReady] = useState(ready);
  const [localUrl, setLocalUrl] = useState(shareUrl);
  const [localPassword, setLocalPassword] = useState(sharePassword);
  const [copiedMessage, setCopiedMessage] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    setLocalReady(ready);
    setLocalUrl(shareUrl);
    setLocalPassword(sharePassword);
  }, [ready, shareUrl, sharePassword]);

  const buildShareMessage = (url: string, pwd: string) => {
    const bodyKey =
      messageVariant === "cycle" ? "v2.home.cycleShareMessage" : "v2.home.pollShareMessage";
    return buildPublicInviteShareMessage(
      t(bodyKey, { url, groupName: groupName ?? "" }),
      pwd
    );
  };

  const resetCopied = (which: "message" | "link") => {
    if (which === "message") {
      setCopiedMessage(true);
      setTimeout(() => setCopiedMessage(false), 2000);
    } else {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const handleCopyMessage = async () => {
    if (!localUrl) return;
    const ok = await copyText(buildShareMessage(localUrl, localPassword));
    if (ok) {
      resetCopied("message");
      success({
        title: t("v2.home.shareCopyMessageSuccess"),
        description: t("v2.home.shareCopyMessageSuccessDesc"),
      });
    } else {
      error({
        title: t("errors.error"),
        description: t("v2.home.shareCopyError"),
      });
    }
  };

  const handleCopyLink = async () => {
    if (!localUrl) return;
    const ok = await copyText(localUrl);
    if (ok) {
      resetCopied("link");
      success({
        title: t("v2.home.shareCopyLinkSuccess"),
        description: t("v2.home.shareCopyLinkSuccessDesc"),
      });
    } else {
      error({
        title: t("errors.error"),
        description: t("v2.home.shareCopyError"),
      });
    }
  };

  const handleWhatsApp = () => {
    if (!localUrl) return;
    openWhatsAppShare(buildShareMessage(localUrl, localPassword));
  };

  const handleNativeShare = async () => {
    if (!localUrl || !navigator.share) return;
    try {
      await navigator.share({
        title: groupName ?? "Fulbito Stats",
        text: buildShareMessage(localUrl, localPassword),
      });
    } catch {
      // user cancelled
    }
  };

  const handleActivate = async () => {
    const trimmed = password.trim();
    if (trimmed.length < 4) return;
    const url = await onSetupPassword(trimmed);
    if (!url) return;
    setLocalUrl(url);
    setLocalPassword(trimmed);
    setLocalReady(true);
    setPassword("");
  };

  const canActivate = password.trim().length >= 4;

  if (!localReady) {
    return (
      <div className="space-y-4">
        <p className="text-xs text-muted-foreground">{t("v2.home.pollShareNewLinkHint")}</p>
        <div className="space-y-2">
          <Label htmlFor="cycle-share-password">{t("v2.home.linkPasswordLabel")}</Label>
          <Input
            id="cycle-share-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("v2.home.linkPasswordPlaceholder")}
            autoFocus
          />
          <p className="text-xs text-muted-foreground">{t("v2.home.pollSharePromptPasswordHint")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            className="bg-amber-600 font-bold text-white hover:bg-amber-600/90"
            disabled={!canActivate || settingUp}
            onClick={handleActivate}
          >
            {settingUp ? t("common.loading") : t("v2.home.shareActivateLink")}
          </Button>
          {showDone && onDone && (
            <Button variant="outline" onClick={onDone}>
              {t("v2.home.pollSharePromptLater")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{t("v2.home.pollShareNewLinkHint")}</p>

      <div className="space-y-2">
        <Label>{t("v2.home.shareLinkLabel")}</Label>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 rounded-lg border bg-muted/30 px-3 py-2.5 text-sm text-muted-foreground break-all">
            {localUrl}
          </div>
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="shrink-0"
            onClick={handleCopyLink}
            aria-label={t("v2.home.shareCopyLink")}
          >
            {copiedLink ? <FaCheck className="h-4 w-4" /> : <FaRegCopy className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {localPassword && (
        <p className="text-sm text-muted-foreground">
          {t("v2.home.sharePasswordReminder", { password: localPassword })}
        </p>
      )}

      <div className="rounded-lg border bg-muted/20 px-3 py-3 text-sm whitespace-pre-wrap break-words">
        {localUrl ? buildShareMessage(localUrl, localPassword) : ""}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Button
          className="bg-amber-600 font-bold text-white hover:bg-amber-600/90"
          disabled={!localUrl}
          onClick={handleCopyMessage}
        >
          {copiedMessage ? <FaCheck className="h-4 w-4" /> : <FaRegCopy className="h-4 w-4" />}
          {copiedMessage ? t("v2.home.shareCopied") : t("v2.home.shareCopyMessage")}
        </Button>
        <Button type="button" variant="outline" disabled={!localUrl} onClick={handleWhatsApp}>
          <FaWhatsapp className="h-4 w-4 text-green-600" />
          {t("v2.home.shareWhatsApp")}
        </Button>
        {canUseNativeShare && (
          <Button type="button" variant="outline" disabled={!localUrl} onClick={handleNativeShare}>
            <LuShare2 className="h-4 w-4" />
            {t("v2.home.shareNative")}
          </Button>
        )}
      </div>

      {showDone && onDone && (
        <Button variant="ghost" className="w-full" onClick={onDone}>
          {t("v2.home.shareDone")}
        </Button>
      )}
    </div>
  );
}
